// Shared claim/reassign dialog (notes + optional route-to-project), extracted from
// AccountDetail. Loads the account's projects itself; writes through useEmailActions
// so every surface's caches update identically.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, type User } from "@/lib/api";
import type { UnifiedEmail } from "@/lib/email/types";
import type { EmailActions } from "./useEmailActions";

interface Props {
  email: UnifiedEmail | null;
  users: User[];
  actions: EmailActions;
  onClose: () => void;
}

// Same "Title — Jul 2026" shape as the pin pickers (EmailRow/InboxOutlook), so a client
// with several same-titled recurring occurrences stays distinguishable here too.
function projectOptionLabel(p: { title: string; period_display?: string | null }): string {
  return p.period_display ? `${p.title} — ${p.period_display}` : p.title;
}

export function ClaimEmailDialog({ email, users, actions, onClose }: Props) {
  const [assignee, setAssignee] = useState("");
  const [projectId, setProjectId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const existingTask = !!email?.taskStatus;

  // Seed form state from the email each time the dialog opens.
  useEffect(() => {
    if (email) {
      setAssignee(email.assignedToUserId ?? "");
      setProjectId("");
      setNotes(email.taskNotes ?? "");
    }
  }, [email?.id]);

  // Active only + same queryKey/queryFn shape as the EmailRow/InboxOutlook pin pickers —
  // they share this cache entry, so the fetches must stay identical or the two pickers
  // nondeterministically swap lists for a minute at a time.
  const { data: projects = [] } = useQuery({
    queryKey: ["account-projects", email?.accountId],
    queryFn: () => api.getProjects({ accountId: email!.accountId, status: "active" }),
    enabled: !!email,
    staleTime: 60_000,
  });

  // A legacy assignee (now non-assignable — e.g. deactivated or contractor) must still
  // render, or the select would silently blank the current owner.
  const legacyAssignee =
    email?.assignedToUserId && !users.some((u) => u.id === email.assignedToUserId)
      ? { id: email.assignedToUserId, name: email.assignedToName ?? "(former assignee)" }
      : null;

  const save = async () => {
    if (!email) return;
    setSaving(true);
    try {
      const user = assignee
        ? { id: assignee, name: users.find((u) => u.id === assignee)?.name ?? legacyAssignee?.name ?? "" }
        : null;
      const result = await actions.upsertTask(email, {
        status: user ? "assigned" : "new",
        assignedToUserId: user?.id ?? null,
        assignedToName: user?.name ?? null,
        notes: notes || null,
      });
      if (result && projectId) {
        const project = projects.find((p: any) => p.id === projectId);
        // Pass the period-labeled title so the optimistic chip/toast match the server overlay.
        await actions.pinToProject(email, { id: projectId, title: project ? projectOptionLabel(project) : "" });
      }
      if (result) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!email} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="w-[480px] max-w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>{existingTask ? "Reassign Email" : "Claim Email"}</DialogTitle>
          <DialogDescription className="text-xs">
            {existingTask
              ? "Update the owner or add notes to this email."
              : "Assign ownership and optionally route to a project."}
          </DialogDescription>
        </DialogHeader>

        {email && (
          <div className="space-y-0.5 overflow-hidden rounded-md bg-muted/50 p-3 text-sm">
            <p className="line-clamp-2 font-medium leading-snug">{email.subject || "(no subject)"}</p>
            <p className="truncate text-xs text-muted-foreground">
              From: {email.fromName || email.fromAddress || "Unknown"}
            </p>
            {email.snippet && <p className="line-clamp-2 text-xs text-muted-foreground">{email.snippet}</p>}
          </div>
        )}

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">Assign to</label>
            <select
              className="w-full cursor-pointer rounded-md border bg-background px-3 py-2 text-sm"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            >
              <option value="">Unassigned</option>
              {legacyAssignee && <option value={legacyAssignee.id}>{legacyAssignee.name}</option>}
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-muted-foreground">
              Route to project <span className="text-xs font-normal">(optional)</span>
            </label>
            <select
              className="w-full cursor-pointer rounded-md border bg-background px-3 py-2 text-sm"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">No project</option>
              {projects.map((p: any) => (
                <option key={p.id} value={p.id}>{projectOptionLabel(p)}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-muted-foreground">
              Notes <span className="text-xs font-normal">(optional)</span>
            </label>
            <Textarea
              rows={3}
              placeholder="Action items, context, follow-up…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="resize-none text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={saving} onClick={save}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : existingTask ? "Reassign" : "Claim Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
